// Packages and components
import React, { useContext, useEffect, useState } from 'react';
import { Box } from 'grommet';
import SHDButton from '@ui/Button/SHDButton';
import SHDHeading from '@ui/Heading/SHDHeading';
import SHDImage from '@ui/Image/SHDImage';
import SHDInput from '@ui/Input/SHDInput';
import SHDLoader from '@ui/Loader/SHDLoader';
import SHDModalContainer from '@ui/ModalContainer/SHDModalContainer';
import SHDSelect from '@ui/Select/SHDSelect';
import SHDText from '@ui/Text/SHDText';

// Helpers and utilities
import { UI } from '@constants/baseUrls';
import {
  useImportTeamPlayers,
  useImportTeamSearch,
  useImportTeamSnap,
} from '@hooks/useTeam';
import * as Sentry from '@sentry/browser';

// Types and enums
import {
  TeamImportPartnerLogo,
  TeamImportPartnerName,
  AgeGroupOption,
} from '@theme/theme.d';
import type { ExternalTeamSearchResultOutput } from '@dto/api';
import type { ImportTeamProps } from './Onboarding.d';
import type { SelectOption } from '@theme/theme.d';
import { AppHostContext } from '@services/appHost';

const ImportTeam: React.FC<ImportTeamProps> = ({
  importPartner,
  sport,
  onContinue,
}) => {
  // States
  const [buttonIndexClicked, setButtonIndexClicked] = useState<number>();
  const [selectedTeam, setSelectedTeam] =
    useState<ExternalTeamSearchResultOutput>();
  const [importComplete, setImportComplete] = useState<boolean>(false);
  const appHost = useContext(AppHostContext);

  const [teamSearchQuery, setTeamSearchQuery] = useState<string>('');
  const [ageGroup, setAgeGroup] = useState<SelectOption>({
    label: '',
    value: '0',
  });

  const [teamSnapHits, setTeamSnapHits] = useState<
    ExternalTeamSearchResultOutput[]
  >([]);

  // Constants
  const ageGroupOptions = [
    {
      label: AgeGroupOption['18U'],
      value: 18,
    },
    {
      label: AgeGroupOption['17U'],
      value: 17,
    },
    {
      label: AgeGroupOption['16U'],
      value: 16,
    },
    {
      label: AgeGroupOption['15U'],
      value: 15,
    },
    {
      label: '14U & under',
      value: 14,
    },
  ];

  // Hooks
  const {
    debouncing,
    results: importTeamSearchResults,
    onClearResults,
    onSearch,
    loading: importTeamSearchLoading,
  } = useImportTeamSearch();

  const { loading: importTeamPlayersLoading, onImportTeamPlayers } =
    useImportTeamPlayers();

  const { onImportTeamSnap, isListeningForAuth, setIsListeningForAuth } =
    useImportTeamSnap();

  // Lifecycle
  useEffect(() => {
    if (importComplete) {
      onContinue && onContinue(selectedTeam as ExternalTeamSearchResultOutput);
    }
  }, [importComplete, onContinue, selectedTeam]);

  // Handlers
  const handleSearchInput = (input: string) => {
    setTeamSearchQuery(input);
    const queryParams = {
      source: importPartner,
      search: input,
      age_group: ageGroup.value || '0',
      sport: sport,
    };

    if (input === '') {
      onClearResults();
      return;
    }
    onSearch(queryParams);
  };

  const handleImportButtonLoading = () => {
    return (
      <Box pad={{ vertical: 'xxxsmall' }} align="center">
        <SHDLoader size={15} color="white" />
      </Box>
    );
  };

  const handleImportTeam = async (teamId: string | number, index: number) => {
    setButtonIndexClicked(index);
    const team = importTeamSearchResults?.hits[
      index
    ] as ExternalTeamSearchResultOutput;
    await onImportTeamPlayers(
      importPartner as TeamImportPartnerName,
      teamId
    ).then((response) => {
      team.players = response?.players;
      setSelectedTeam(team);
      setImportComplete(true);
    });
  };

  const handleTeamSnapAuth = async () => {
    const maxRetries = appHost.isHosted ? 15 : 3;
    let retryCount = 0;

    while (retryCount < maxRetries) {
      retryCount++;
      try {
        const response = await onImportTeamSnap();
        if (response?.hits?.length > 0) {
          setTeamSnapHits(response.hits as ExternalTeamSearchResultOutput[]);
          return;
        } else if (response?.hits[0]) {
          setSelectedTeam(response.hits[0] as ExternalTeamSearchResultOutput);
          setImportComplete(true);
          return;
        }
      } catch (error) {
        Sentry.captureException(error);
      }
      await new Promise((resolve) =>
        setTimeout(resolve, appHost.isHosted ? 2000 : 1000)
      );
    }

    setSelectedTeam(undefined);
    setImportComplete(false);
  };

  const handleTeamSnapImport = () => {
    const clientId = import.meta.env.VITE_APP_TEAM_SNAP_CLIENT_ID;
    const redirectUri = `${appHost.isHosted ? 'sidelinehd:/' : UI}/teamsnap/callback`;
    const tsUrl = `https://auth.teamsnap.com/oauth/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=token`;
    window.open(tsUrl, '_blank');
    setIsListeningForAuth(true);
    handleTeamSnapAuth();
  };

  // Renderers
  const renderImportTeamSearch = () => {
    return (
      <>
        <SHDInput
          label="Team name"
          value={teamSearchQuery}
          onChange={handleSearchInput}
        />

        {debouncing || importTeamSearchLoading ? (
          <Box width="100%" align="center">
            <SHDLoader size={24} color="brand-800" />
          </Box>
        ) : (
          <>
            <Box gap="xlarge">
              {importTeamSearchResults &&
                importTeamSearchResults.hits?.map((team, index) => (
                  <Box
                    key={index}
                    flex={true}
                    direction="row"
                    justify="between"
                    align="center"
                    margin={{ bottom: 'xxsmall' }}
                  >
                    <Box>
                      <SHDText style="subhead-bold">{team.name}</SHDText>
                      <Box direction="row" wrap={false} gap="small">
                        {team.ageGroup && (
                          <SHDText style="subhead" color="gray-600">
                            {team.ageGroup}
                          </SHDText>
                        )}
                        {team.age_group && (
                          <SHDText style="subhead" color="gray-600">
                            {team.age_group}
                          </SHDText>
                        )}
                        <SHDText style="subhead" color="gray-600">
                          est. {team.season}
                        </SHDText>
                      </Box>
                    </Box>
                    <Box width="5.625rem">
                      <SHDButton
                        tertiary
                        label={
                          index === buttonIndexClicked
                            ? handleImportButtonLoading()
                            : 'Import'
                        }
                        disabled={importTeamPlayersLoading}
                        onClick={() => {
                          handleImportTeam(team.id, index);
                        }}
                      />
                    </Box>
                  </Box>
                ))}
            </Box>
          </>
        )}
      </>
    );
  };

  const renderImportTeamSnapButton = () => {
    return (
      <Box gap="xlarge">
        {!teamSnapHits.length ? (
          <>
            <SHDText style="subhead">
              When you press the button below, you will be redirected to
              TeamSnap to log in.
            </SHDText>
            <SHDButton
              dark
              disabled={isListeningForAuth}
              label={
                isListeningForAuth
                  ? handleImportButtonLoading()
                  : 'Import from TeamSnap'
              }
              icon={
                <SHDImage
                  src={TeamImportPartnerLogo.teamsnap}
                  alt="TeamSnap logo"
                  height={isListeningForAuth ? 0 : 32}
                  width={isListeningForAuth ? 0 : 32}
                />
              }
              onClick={() => {
                handleTeamSnapImport();
              }}
            />
          </>
        ) : (
          <>
            {teamSnapHits?.map((team, index) => (
              <Box
                key={index}
                flex={true}
                direction="row"
                justify="between"
                align="center"
                margin={{ bottom: 'xxsmall' }}
              >
                <Box>
                  <SHDText style="subhead-semibold">{team.name}</SHDText>
                  <Box direction="row" wrap={false} gap="small">
                    {team.ageGroup && (
                      <SHDText style="subhead" color="gray-600">
                        {team.ageGroup}
                      </SHDText>
                    )}
                    {team.age_group && (
                      <SHDText style="subhead" color="gray-600">
                        {team.age_group}
                      </SHDText>
                    )}
                    <SHDText style="subhead" color="gray-600">
                      est. {team.season}
                    </SHDText>
                  </Box>
                </Box>
                <Box width="5.625rem">
                  <SHDButton
                    tertiary
                    label={
                      index === buttonIndexClicked
                        ? handleImportButtonLoading()
                        : 'Import'
                    }
                    disabled={index === buttonIndexClicked}
                    onClick={() => {
                      setButtonIndexClicked(index);
                      setSelectedTeam(
                        teamSnapHits[index] as ExternalTeamSearchResultOutput
                      );
                      setImportComplete(true);
                    }}
                  />
                </Box>
              </Box>
            ))}
          </>
        )}
      </Box>
    );
  };

  const renderImportTeamForm = () => {
    switch (importPartner) {
      case 'aes':
        return (
          <>
            <SHDSelect
              options={ageGroupOptions}
              value={ageGroup}
              label="Age group"
              onChange={(selection) => setAgeGroup(selection)}
            />
            {renderImportTeamSearch()}
          </>
        );
      case 'teamsnap':
        return <>{renderImportTeamSnapButton()}</>;
      case 'usssa':
      case 'pg':
      default:
        return <>{renderImportTeamSearch()}</>;
    }
  };

  return (
    <SHDModalContainer>
      <Box gap="xlarge">
        <Box gap="xxsmall">
          <SHDHeading level={3} formatCasing={false}>
            {importPartner
              ? `Import your ${TeamImportPartnerName[importPartner]} team`
              : `Import your team`}
          </SHDHeading>
          <SHDText color="gray-900-alpha-80" style="subhead">
            Importing a team will automatically fill in your team&apos;s info
            and roster.
          </SHDText>
        </Box>
        {renderImportTeamForm()}
      </Box>
    </SHDModalContainer>
  );
};

export default ImportTeam;
